import { Component, ElementRef, Inject, OnDestroy, OnInit, Optional, ViewChild, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { GameSearchModalData, GameSearchQueryDTO, GameSearchResult } from './game-search.model';
import { GameSearchService } from './game-search.service';

@Component({
  selector: 'app-game-search-modal',
  templateUrl: './game-search-modal.component.html',
  styleUrls: ['./game-search-modal.component.scss'],
  standalone: false,
})
export class GameSearchModalComponent implements OnInit, OnDestroy {
  @ViewChild('resultsContainer') resultsContainer: ElementRef<HTMLDivElement>;

  readonly searchItemValue = signal('');
  readonly searchQueryData = signal<GameSearchQueryDTO | null>(null);
  readonly isLoading = signal(false);
  readonly loadingMore = signal(false);
  readonly resolving = signal(false);
  readonly noResultsFound = signal(false);
  readonly showInfinite = signal(true);

  private readonly searchTerm$ = new Subject<string>();
  private searchSubscription: Subscription;
  private page = 0;

  readonly tips = [
    { prefix: 'Names like', example: 'Big Bass Bonanza' },
    { prefix: 'Partial matches like', example: 'Megaways' },
    { prefix: 'Studio names like', example: 'Pragmatic Play' },
  ];

  constructor(
    private dialogRef: MatDialogRef<GameSearchModalComponent>,
    private gameSearchService: GameSearchService,
    @Optional() @Inject(MAT_DIALOG_DATA) private data: GameSearchModalData
  ) {}

  ngOnInit(): void {
    this.subscribeToSearch();
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  onSearchInput(value: string): void {
    const trimmed = (value ?? '').trim();
    this.searchItemValue.set(value ?? '');

    if (!trimmed) {
      this.searchTerm$.next('');
      this.searchQueryData.set(null);
      this.noResultsFound.set(false);
      this.isLoading.set(false);
      return;
    }

    this.searchTerm$.next(trimmed);
  }

  clearInput(): void {
    this.searchItemValue.set('');
    this.searchTerm$.next('');
    this.searchQueryData.set(null);
    this.noResultsFound.set(false);
    this.isLoading.set(false);
  }

  setSearchTip(example: string): void {
    this.searchItemValue.set(example);
    this.onSearchInput(example);
  }

  selectGame(game: GameSearchResult): void {
    const fallback = this.mapSearchResult(game);
    const resolveUrl = this.data?.resolveUrl;

    if (!resolveUrl) {
      this.dialogRef.close(fallback);
      return;
    }

    this.resolving.set(true);
    this.gameSearchService.resolveGameInstance(resolveUrl, game.id).subscribe({
      next: (fullGame) => {
        this.resolving.set(false);
        this.dialogRef.close(fullGame ? this.normalizeResolvedGame(fullGame, fallback) : fallback);
      },
      error: () => {
        this.resolving.set(false);
        this.dialogRef.close(fallback);
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  onResultsScroll(): void {
    if (!this.showInfinite() || this.isLoading() || this.loadingMore() || !this.searchQueryData()) {
      return;
    }

    const el = this.resultsContainer?.nativeElement;
    if (!el) {
      return;
    }

    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 80;
    if (!nearBottom) {
      return;
    }

    this.loadMore();
  }

  private subscribeToSearch(): void {
    this.searchSubscription = this.searchTerm$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        tap((query) => {
          this.page = 0;
          this.showInfinite.set(true);
          this.isLoading.set(!!query);
        }),
        switchMap((query) => (query ? this.gameSearchService.search(query, 0) : of(null)))
      )
      .subscribe({
        next: (results) => {
          if (!results) {
            this.isLoading.set(false);
            this.noResultsFound.set(false);
            this.searchQueryData.set(null);
            return;
          }
          this.applySearchResults(results);
        },
        error: () => {
          this.isLoading.set(false);
          this.noResultsFound.set(true);
          this.searchQueryData.set(null);
        },
      });
  }

  private mapSearchResult(game: GameSearchResult): GameSearchResult {
    return {
      ...game,
      logoUrl: this.toRelativeAssetPath(game.imageUrl || game.logoUrl),
      provider: game.studio || game.provider,
      studioName: game.studio || game.studioName,
    };
  }

  private normalizeResolvedGame(fullGame: any, fallback: GameSearchResult): any {
    const logoUrl = this.toRelativeAssetPath(fullGame.logoUrl || fullGame.imageUrl || fallback.logoUrl);
    return {
      ...fullGame,
      logoUrl,
      provider: fullGame.provider || fallback.provider,
      studioName: fullGame.studioName || fullGame.studio || fallback.studioName,
      status: fullGame.status || 'ACTIVE',
    };
  }

  private toRelativeAssetPath(url: string): string {
    if (!url) {
      return url;
    }
    if (url.startsWith('./assets/')) {
      return url;
    }
    const marker = '/assets/';
    const idx = url.indexOf(marker);
    if (idx >= 0) {
      return '.' + url.substring(idx);
    }
    return url;
  }

  private loadMore(): void {
    const query = this.searchItemValue().trim();
    const currentResults = this.searchQueryData();
    if (!query || !currentResults) {
      return;
    }

    if (currentResults.games.length >= currentResults.total) {
      this.showInfinite.set(false);
      return;
    }

    this.loadingMore.set(true);
    this.page += 1;
    const offset = this.page * this.gameSearchService.pageSize;

    this.gameSearchService.search(query, offset).subscribe({
      next: (result) => {
        const existing = this.searchQueryData();
        if (result?.games?.length && existing) {
          this.searchQueryData.set({
            ...result,
            games: [...existing.games, ...result.games],
          });
        } else {
          this.showInfinite.set(false);
        }
        this.loadingMore.set(false);
      },
      error: () => {
        this.loadingMore.set(false);
        this.showInfinite.set(false);
      },
    });
  }

  private applySearchResults(results: GameSearchQueryDTO): void {
    this.isLoading.set(false);
    this.noResultsFound.set(!results?.games?.length);
    this.searchQueryData.set(results);
    this.showInfinite.set(!!results?.games?.length && results.games.length < results.total);
  }
}
